import { useEffect, useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { portalApi, usePortal } from "@/lib/PortalContext";
import { formatErr } from "@/lib/api";
import { toast } from "sonner";
import { ArrowLeft, Lock, Eye, EyeOff, LogOut, Check, Shield, X } from "lucide-react";

export default function PortalAccountPage() {
  const nav = useNavigate();
  const { client, setPortalClient, logout } = usePortal();
  const [busy, setBusy] = useState(false);
  const [mode, setMode] = useState(null); // 'create' | 'change' | 'remove'
  const [current, setCurrent] = useState("");
  const [next, setNext] = useState("");
  const [confirm, setConfirm] = useState("");
  const [showPw, setShowPw] = useState(false);
  const [removeConfirm, setRemoveConfirm] = useState(false);

  useEffect(() => {
    if (client === null) nav("/portal/login");
  }, [client, nav]);

  const refresh = async () => {
    const me = await portalApi.get("/portal/me");
    setPortalClient(me.data);
  };

  const reset = () => { setCurrent(""); setNext(""); setConfirm(""); setMode(null); setRemoveConfirm(false); };

  const createPw = async (e) => {
    e.preventDefault();
    if (next.length < 12) return toast.error("Password must be at least 12 characters");
    if (next !== confirm) return toast.error("Passwords don't match");
    setBusy(true);
    try {
      await portalApi.post("/portal/create-password", { password: next });
      toast.success("Password created. You can sign in with email + password next time.");
      reset(); await refresh();
    } catch (e) { toast.error(formatErr(e)); }
    finally { setBusy(false); }
  };

  const changePw = async (e) => {
    e.preventDefault();
    if (next.length < 12) return toast.error("New password must be at least 12 characters");
    if (next !== confirm) return toast.error("Passwords don't match");
    setBusy(true);
    try {
      await portalApi.post("/portal/change-password", { current_password: current, new_password: next });
      toast.success("Password updated");
      reset();
    } catch (e) { toast.error(formatErr(e)); }
    finally { setBusy(false); }
  };

  const removePw = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await portalApi.post("/portal/remove-password", { current_password: current });
      toast.success("Password removed. You'll sign in via magic link only.");
      reset(); await refresh();
    } catch (e) { toast.error(formatErr(e)); }
    finally { setBusy(false); }
  };

  if (!client) return null;
  const hasPw = !!client.has_password;

  return (
    <div className="min-h-screen bg-[color:hsl(var(--background))]" data-testid="portal-account">
      <header className="border-b border-border">
        <div className="max-w-3xl mx-auto px-6 py-6 flex justify-between items-center">
          <Link to="/portal" className="text-xs text-muted-foreground hover:text-foreground inline-flex items-center gap-1" data-testid="link-back-portal">
            <ArrowLeft className="w-3 h-3" strokeWidth={1.5}/> Back to portal
          </Link>
          <button onClick={async () => { await logout(); nav("/"); }} className="text-xs text-muted-foreground inline-flex gap-1" data-testid="btn-account-logout"><LogOut className="w-3 h-3" strokeWidth={1.5}/> Sign out</button>
        </div>
      </header>

      <main className="max-w-3xl mx-auto px-6 py-14">
        <div className="overline">Account</div>
        <h1 className="font-serif font-light mt-3" style={{ fontSize: "clamp(36px, 5vw, 48px)", lineHeight: 1.05 }}>Account settings</h1>
        <p className="text-muted-foreground mt-3">Signed in as <strong>{client.email}</strong>.</p>

        <section className="mt-10">
          <div className="overline">Sign-in</div>
          <div className="luxe-card p-6 mt-3 flex items-start gap-4" data-testid="password-status-card">
            <Shield className="w-6 h-6 mt-1 shrink-0" style={{ color: hasPw ? "#3A4D39" : "#A68A64" }} strokeWidth={1.5}/>
            <div className="flex-1 min-w-0">
              <div className="font-serif text-xl">
                {hasPw ? "Password sign-in is enabled" : "You're using magic-link sign-in"}
              </div>
              <p className="text-sm text-muted-foreground mt-1">
                {hasPw
                  ? "You can sign in with email + password. Magic links still work as a backup."
                  : "Every sign-in requires a one-time link sent by email. You can add a password for faster access."
                }
              </p>
              {!mode && (
                <div className="mt-4 flex gap-2 flex-wrap">
                  {!hasPw && <button data-testid="btn-mode-create" onClick={() => setMode("create")} className="btn-primary text-xs">Create password</button>}
                  {hasPw && <button data-testid="btn-mode-change" onClick={() => setMode("change")} className="btn-outline text-xs">Change password</button>}
                  {hasPw && <button data-testid="btn-mode-remove" onClick={() => { setMode("remove"); setRemoveConfirm(false); }} className="btn-outline text-xs">Remove password</button>}
                </div>
              )}
            </div>
          </div>

          {mode === "create" && (
            <form onSubmit={createPw} className="luxe-card p-6 mt-4 space-y-4" data-testid="form-create-pw">
              <div className="flex justify-between items-center">
                <div className="overline">Create a password</div>
                <button type="button" onClick={reset} className="text-muted-foreground p-1"><X className="w-4 h-4" strokeWidth={1.5}/></button>
              </div>
              <PwField label="New password" value={next} onChange={setNext} show={showPw} setShow={setShowPw} testid="create-new"/>
              <PwField label="Confirm password" value={confirm} onChange={setConfirm} show={showPw} setShow={setShowPw} testid="create-confirm"/>
              <PwRules pw={next} confirm={confirm}/>
              <button type="submit" disabled={busy} data-testid="btn-submit-create" className="btn-primary w-full justify-center">{busy ? "Saving…" : "Save password"}</button>
            </form>
          )}

          {mode === "change" && (
            <form onSubmit={changePw} className="luxe-card p-6 mt-4 space-y-4" data-testid="form-change-pw">
              <div className="flex justify-between items-center">
                <div className="overline">Change your password</div>
                <button type="button" onClick={reset} className="text-muted-foreground p-1"><X className="w-4 h-4" strokeWidth={1.5}/></button>
              </div>
              <PwField label="Current password" value={current} onChange={setCurrent} show={showPw} setShow={setShowPw} autoComplete="current-password" testid="change-current"/>
              <PwField label="New password" value={next} onChange={setNext} show={showPw} setShow={setShowPw} testid="change-new"/>
              <PwField label="Confirm new password" value={confirm} onChange={setConfirm} show={showPw} setShow={setShowPw} testid="change-confirm"/>
              <PwRules pw={next} confirm={confirm}/>
              <button type="submit" disabled={busy} data-testid="btn-submit-change" className="btn-primary w-full justify-center">{busy ? "Saving…" : "Update password"}</button>
            </form>
          )}

          {mode === "remove" && (
            <form onSubmit={removePw} className="luxe-card p-6 mt-4 space-y-4" data-testid="form-remove-pw">
              <div className="flex justify-between items-center">
                <div className="overline">Remove your password</div>
                <button type="button" onClick={reset} className="text-muted-foreground p-1"><X className="w-4 h-4" strokeWidth={1.5}/></button>
              </div>
              <p className="text-sm text-muted-foreground">You'll return to magic-link-only sign-in. You can add a password again anytime.</p>
              <PwField label="Enter current password to confirm" value={current} onChange={setCurrent} show={showPw} setShow={setShowPw} autoComplete="current-password" testid="remove-current"/>
              <label className="flex items-start gap-2 text-sm">
                <input type="checkbox" checked={removeConfirm} onChange={(e) => setRemoveConfirm(e.target.checked)} data-testid="checkbox-remove-confirm"/>
                <span>Yes, remove my password and return to magic-link sign-in.</span>
              </label>
              <button type="submit" disabled={busy || !removeConfirm} data-testid="btn-submit-remove" className="btn-outline w-full justify-center" style={{ borderColor: "hsl(var(--destructive))", color: "hsl(var(--destructive))" }}>
                {busy ? "Removing…" : "Remove password"}
              </button>
            </form>
          )}
        </section>
      </main>
    </div>
  );
}

function PwField({ label, value, onChange, show, setShow, testid, autoComplete = "new-password" }) {
  return (
    <div>
      <label className="overline text-[10px] block mb-1">{label}</label>
      <div className="relative">
        <Lock className="w-3.5 h-3.5 absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" strokeWidth={1.5}/>
        <input
          data-testid={`input-${testid}`}
          type={show ? "text" : "password"} autoComplete={autoComplete}
          className="luxe-input pl-9 pr-10"
          value={value} onChange={(e) => onChange(e.target.value)}
          required
        />
        <button type="button" tabIndex={-1} onClick={() => setShow((v) => !v)} className="absolute right-2 top-1/2 -translate-y-1/2 p-1 text-muted-foreground" aria-label="Toggle password visibility">
          {show ? <EyeOff className="w-4 h-4" strokeWidth={1.5}/> : <Eye className="w-4 h-4" strokeWidth={1.5}/>}
        </button>
      </div>
    </div>
  );
}

function PwRules({ pw, confirm }) {
  const rules = [
    { ok: pw.length >= 12, label: "At least 12 characters" },
    { ok: new Set(pw).size >= 3, label: "Not too repetitive" },
    { ok: pw === confirm && pw.length > 0, label: "Both fields match" },
  ];
  return (
    <ul className="text-xs space-y-1">
      {rules.map((r) => (
        <li key={r.label} className={`flex items-center gap-2 ${r.ok ? "text-foreground" : "text-muted-foreground"}`}>
          <Check className={`w-3 h-3 ${r.ok ? "opacity-100" : "opacity-30"}`} strokeWidth={2}/> {r.label}
        </li>
      ))}
    </ul>
  );
}
